// ─── reportApi ────────────────────────────────────────────────────────────────
// Thin wrapper around /api/data/reports/ — used by the Report tab on the
// dashboard.
//
//   GET    /api/data/reports/?project_id=<id>     list user's reports in project
//   POST   /api/data/reports/                     create
//   GET    /api/data/reports/<id>/                fetch one
//   PATCH  /api/data/reports/<id>/                update name/settings
//   DELETE /api/data/reports/<id>/                soft-delete
//
// All endpoints are scoped server-side to (project_id, request.user.id), so
// a report saved by user A in project P is invisible to user B AND to user
// A in project Q. Same auth + cache-buster pattern as validateApi.js.
// ──────────────────────────────────────────────────────────────────────────────

import axios from 'axios'

const API_URL         = import.meta.env.VITE_API_URL          || 'https://wqlive.ezxdemo.com'
const API_BASE_PATH   = import.meta.env.VITE_API_BASE_PATH    || '/api'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY  || 'auth_token'

const authHeaders = () => {
  const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

// Cache-bust on GETs — same fix as validateApi.js. Stale "I deleted that
// report but it's still in the dropdown" reads were the original symptom.
const getHeaders = () => ({
  ...authHeaders(),
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma':        'no-cache',
  'Expires':       '0',
})

const url = (p) => `${API_URL}${API_BASE_PATH}/data${p}`

export async function listReports(projectId) {
  const res = await axios.get(url('/reports/'), {
    headers: getHeaders(),
    params:  { project_id: projectId, _t: Date.now() },
  })
  return res.data
}

export async function createReport({ projectId, name, settings }) {
  const res = await axios.post(url('/reports/'),
    { project_id: projectId, name, settings: settings || {} },
    { headers: authHeaders() })
  return res.data
}

export async function getReport(reportId) {
  const res = await axios.get(url(`/reports/${reportId}/`), {
    headers: getHeaders(),
    params:  { _t: Date.now() },
  })
  return res.data
}

export async function updateReport(reportId, { name, settings }) {
  const body = {}
  if (name     !== undefined) body.name = name
  if (settings !== undefined) body.settings = settings
  const res = await axios.patch(url(`/reports/${reportId}/`), body,
    { headers: authHeaders() })
  return res.data
}

export async function deleteReport(reportId) {
  const res = await axios.delete(url(`/reports/${reportId}/`),
    { headers: authHeaders() })
  return res.data
}

// ─── External exports (GLOS / NDBC) ──────────────────────────────────────────
// POSTs the report's observations to the chosen network with the user-
// supplied mapping. Server does the actual outbound HTTP call so the
// API key never lives in the browser; this is just the client wrapper.

export async function exportReportGlos(reportId, body) {
  const res = await axios.post(url(`/reports/${reportId}/export/glos/`), body,
    { headers: authHeaders(), timeout: 60000 })
  return res.data
}

export async function exportReportNdbc(reportId, body) {
  const res = await axios.post(url(`/reports/${reportId}/export/ndbc/`), body,
    { headers: authHeaders(), timeout: 60000 })
  return res.data
}

// Report → Share via Email. Body may carry a base64 PDF/CSV attachment, so a
// long range (a month, many parameters, chart image) makes the upload + email
// send take well over 30s — allow up to 2 minutes before giving up.
export async function shareReportEmail(reportId, body) {
  const res = await axios.post(url(`/reports/${reportId}/share/email/`), body,
    { headers: authHeaders(), timeout: 120000 })
  return res.data
}

// Report → Share via FTP (uploads the report attachment to an FTP server).
export async function shareReportFtp(reportId, body) {
  const res = await axios.post(url(`/reports/${reportId}/share/ftp/`), body,
    { headers: authHeaders(), timeout: 120000 })
  return res.data
}

// NDBC tag catalogue from the NDBCTag table — drives the Tag dropdown
// in the NDBC export modal. Cached on the modal level since the list
// rarely changes.
export async function listNdbcTags() {
  const res = await axios.get(url('/reports/export/ndbc/tags/'),
    { headers: authHeaders(), timeout: 15000 })
  return res.data?.tags || []
}

// ─── Schedules — list / create / update / delete ──────────────────────────────
// List every schedule for the project. Optional `report_id` narrows to
// one report; `enabled` filters by state.
export async function listSchedules(projectId, extra = {}) {
  const res = await axios.get(url('/reports/schedules/'), {
    headers: authHeaders(),
    params: { project_id: projectId, ...extra, _t: Date.now() },
  })
  return res.data?.schedules || []
}
// Create a new schedule for `reportId`. Body must include a `transport`
// (email / ftp / glos / ndbc) plus the timing fields (days_of_week,
// send_at, enabled, only_new_data) and a `config` object for the
// transport-specific settings.
export async function createSchedule(reportId, body) {
  const res = await axios.post(url(`/reports/${reportId}/schedules/`), body,
    { headers: authHeaders() })
  return res.data
}
export async function updateSchedule(scheduleId, body) {
  const res = await axios.patch(url(`/reports/schedules/${scheduleId}/`), body,
    { headers: authHeaders() })
  return res.data
}
export async function deleteSchedule(scheduleId) {
  const res = await axios.delete(url(`/reports/schedules/${scheduleId}/`),
    { headers: authHeaders() })
  return res.data
}

export default {
  listReports, createReport, getReport, updateReport, deleteReport,
  exportReportGlos, exportReportNdbc, listNdbcTags,
  shareReportEmail, shareReportFtp,
  listSchedules, createSchedule, updateSchedule, deleteSchedule,
}
